import React, { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../ui/dialog";
import { Globe, Zap, Megaphone, LineChart, Repeat, Check } from "lucide-react";
import toast from "react-hot-toast";

/**
 * The four DOMA attribution categories an agent can assign to a sale. Keys match
 * the backend `Property.PORTAL_ATTRIBUTION_CHOICES`.
 */
export const PORTAL_ATTRIBUTION_CATEGORIES = [
    {
        key: "direct",
        label: "Direct Portal Sale",
        blurb: "Buyer discovered & purchased the property inside the portal.",
        icon: Zap,
        chip: "bg-teal-100 text-teal-600",
        selected: "border-teal-500 ring-2 ring-teal-200 bg-teal-50",
    },
    {
        key: "assisted",
        label: "Portal Assisted Sale",
        blurb: "Buyer came from another source but the portal influenced the deal.",
        icon: Megaphone,
        chip: "bg-indigo-100 text-indigo-600",
        selected: "border-indigo-500 ring-2 ring-indigo-200 bg-indigo-50",
    },
    {
        key: "analytics",
        label: "Analytics Influenced Sale",
        blurb: "Portal surfaced the buyer’s intent and an agent followed up.",
        icon: LineChart,
        chip: "bg-amber-100 text-amber-600",
        selected: "border-amber-500 ring-2 ring-amber-200 bg-amber-50",
    },
    {
        key: "returning",
        label: "Returning Buyer Sale",
        blurb: "A prior Collings buyer purchased again while still engaged.",
        icon: Repeat,
        chip: "bg-emerald-100 text-emerald-600",
        selected: "border-emerald-500 ring-2 ring-emerald-200 bg-emerald-50",
    },
];

const CATEGORY_LABELS = PORTAL_ATTRIBUTION_CATEGORIES.reduce((acc, c) => {
    acc[c.key] = c.label;
    return acc;
}, {});

/**
 * Portal attribution control for the CRM property page.
 *
 * Ticking "Sold through the Collings portal?" opens a popup with the four DOMA
 * categories. Choosing one tags the sale (`sold_through_collings_portal=true`)
 * and records the category in a single action. Unticking clears both.
 */
const PortalAttributionPicker = ({
    soldThroughPortal = false,
    selectedCategory = "",
    onSelect,
    onClear,
    disabled = false,
}) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const isTagged = soldThroughPortal || Boolean(selectedCategory);

    const handleCheckbox = async (e) => {
        if (saving) return;
        if (e.target.checked) {
            // Ticking opens the popup; nothing is saved until a category is chosen.
            setModalOpen(true);
        } else if (typeof onClear === "function") {
            try {
                setSaving(true);
                await onClear();
            } catch (err) {
                console.error("Failed to clear portal attribution:", err);
                toast.error("Failed to clear portal attribution");
            } finally {
                setSaving(false);
            }
        }
    };

    const handleChoose = async (key) => {
        if (typeof onSelect !== "function" || saving) return;
        try {
            setSaving(true);
            await onSelect(key);
            setModalOpen(false);
        } catch (err) {
            console.error("Failed to set attribution category:", err);
            toast.error("Failed to set attribution category");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="w-full">
            <label
                htmlFor="sold-through-portal-toggle"
                className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer select-none transition-colors ${
                    isTagged
                        ? "bg-teal-50 border-teal-200"
                        : "bg-gray-50 border-gray-100 hover:border-gray-200"
                } ${disabled || saving ? "opacity-60 cursor-not-allowed" : ""}`}
            >
                <input
                    id="sold-through-portal-toggle"
                    type="checkbox"
                    checked={isTagged}
                    onChange={handleCheckbox}
                    disabled={disabled || saving}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-[#55C5D0] focus:ring-[#55C5D0] cursor-pointer"
                />
                <span className="min-w-0">
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-800">
                        <Globe className="w-3.5 h-3.5 text-[#55C5D0] flex-shrink-0" />
                        Sold through the Collings portal?
                    </span>
                    <span className="block text-xs font-medium text-gray-400 mt-0.5">
                        {saving
                            ? "Saving..."
                            : "Tick to attribute this sale to the Collings buyer portal."}
                    </span>

                    {/* Show the chosen category + a Change link once tagged */}
                    {isTagged && (
                        <span className="mt-2 flex flex-wrap items-center gap-2">
                            {selectedCategory ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-white border border-teal-200 px-2.5 py-1 text-[11px] font-semibold text-teal-700">
                                    <Check className="w-3 h-3" />
                                    {CATEGORY_LABELS[selectedCategory] || "Categorised"}
                                </span>
                            ) : (
                                <span className="inline-flex items-center rounded-full bg-amber-50 border border-amber-200 px-2.5 py-1 text-[11px] font-semibold text-amber-700">
                                    Not categorised
                                </span>
                            )}
                            <button
                                type="button"
                                onClick={(ev) => {
                                    ev.preventDefault();
                                    setModalOpen(true);
                                }}
                                className="text-[11px] font-semibold text-[#55C5D0] hover:underline"
                            >
                                {selectedCategory ? "Change" : "Pick a category"}
                            </button>
                        </span>
                    )}
                </span>
            </label>

            {/* Popup with the four category choices */}
            <Dialog open={modalOpen} onOpenChange={(open) => !saving && setModalOpen(open)}>
                <DialogContent className="sm:max-w-[480px] rounded-2xl border-none shadow-2xl">
                    <DialogHeader>
                        <div className="flex items-center gap-4">
                            <div className="p-3 rounded-2xl bg-[#55C5D0]/15 text-[#55C5D0]">
                                <Globe className="w-6 h-6" />
                            </div>
                            <div>
                                <DialogTitle className="text-xl font-bold text-gray-900 leading-tight">
                                    How did DOMA contribute?
                                </DialogTitle>
                                <p className="text-xs font-medium text-gray-400 mt-1">
                                    Pick the category this sale appears under on the analytics dashboard.
                                </p>
                            </div>
                        </div>
                    </DialogHeader>

                    <div className="py-2 space-y-2">
                        {PORTAL_ATTRIBUTION_CATEGORIES.map((cat) => {
                            const Icon = cat.icon;
                            const selected = selectedCategory === cat.key;
                            return (
                                <button
                                    key={cat.key}
                                    type="button"
                                    onClick={() => handleChoose(cat.key)}
                                    disabled={saving}
                                    aria-pressed={selected}
                                    className={`w-full text-left rounded-xl border p-3 transition-all disabled:opacity-60 ${
                                        selected
                                            ? cat.selected
                                            : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${cat.chip}`}>
                                            <Icon className="h-4 w-4" />
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-bold text-gray-800">{cat.label}</p>
                                            <p className="text-[11px] leading-snug text-gray-500 mt-0.5">
                                                {cat.blurb}
                                            </p>
                                        </div>
                                        {selected && <Check className="h-4 w-4 flex-shrink-0 text-gray-700" />}
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    <DialogFooter>
                        <button
                            type="button"
                            onClick={() => setModalOpen(false)}
                            disabled={saving}
                            className="w-full px-6 py-3 text-xs font-black uppercase tracking-widest text-gray-500 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all disabled:opacity-50"
                        >
                            Cancel
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PortalAttributionPicker;
